import {
  Access,
  AssetCheckException,
  ExceptionType,
  ListExceptionGroupsDocument,
  SpaceCheckException,
  Tag,
} from "~/operations";
import { Exceptions } from "~/components/exceptions/exceptions";
import { usePolicyOutletContext } from "~/components/policy-gql/policy-gql";
import {
  Box,
  Table,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link, useParams } from "react-router-dom";
import { CheckStats } from "~/components/impact";
import getQueryMrn from "../../lib/getQueryMrn";
import { Header } from "~/types/table";
import { ImpactUpdated } from "~/components/impact/Updated/impact-updated";
import { Clamp } from "../clamp/clamp";
import { FirewatchChip } from "../FirewatchRow/FirewatchChip";
import { ExceptionGroup } from "~/pages/findings/Exceptions/types";

const tableHeaders: Header[] = [
  { id: "IMPACT", label: "Impact" },
  { id: "SCORE", label: "Scores", options: { width: 150 } },
  { id: "QUERY", label: "Check" },
  {
    id: "ASSETS",
    label: "Assets",
    options: { textAlign: "right", width: 150 },
  },
];

export const ExceptionsWrapper = () => {
  const { space, exceptionGroups, policy, isLoading } =
    usePolicyOutletContext();
  const isPrivatePolicy = policy.access === Access.Private;
  const { policyId } = useParams();

  return (
    <Exceptions
      findingMrn={policy.mrn}
      exceptionTypes={[ExceptionType.Security]}
      isLoading={isLoading}
      emptyStateType="check-exceptions"
      refetchQueries={[ListExceptionGroupsDocument]}
      exceptionGroups={exceptionGroups}
      hideViewSwitcher
      renderTable={function (exceptionGroup: ExceptionGroup) {
        const { exceptions } = exceptionGroup;

        return (
          <>
            <Table
              sx={{
                borderRadius: 1,
                overflow: "hidden",
                "& tr:last-child td": {
                  borderBottom: "none",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "background.lighter",
                    "&:hover": { backgroundColor: "background.lighter" },
                  }}
                >
                  {tableHeaders.map((header: Header) => (
                    <TableCell key={header.id}>{header.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {(
                exceptions as (SpaceCheckException | AssetCheckException)[]
              ).map((exception) => {
                const { mrn, riskValue, rating, title } = exception;

                // check the type of exception
                const isAssetException =
                  exception.__typename === "AssetCheckException";
                const isSpaceException =
                  exception.__typename === "SpaceCheckException";

                const policyNameTag: Tag = {
                  key: "policy",
                  value: exception.policyName,
                  __typename: "Tag",
                };

                const uid = mrn.split("/").pop();

                // needs different link for private policies
                let namespace = isPrivatePolicy ? space.id : "mondoohq";

                const checkMrn = getQueryMrn({ namespace, uid: uid as string });

                let assetId = "";
                if (isAssetException) {
                  assetId = exceptionGroup.scopeMrn.split("/").pop() as string;
                }

                const spaceExceptionLink = `/space/findings/check?spaceId=${space.id}&findingMrn=${checkMrn}&policyId=${policyId}`;
                const assetExceptionLink = `/space/inventory/${assetId}/check?checkMrn=${checkMrn}&spaceId=${space.id}`;
                const urlLink = isSpaceException
                  ? spaceExceptionLink
                  : assetExceptionLink;

                return (
                  <TableRow
                    key={mrn}
                    sx={{
                      cursor: "pointer",
                      backgroundColor: "background.light",
                    }}
                  >
                    <TableCell>
                      {rating && (
                        <ImpactUpdated
                          impact={{ rating, value: riskValue ?? 0 }}
                          isRiskScore
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {isSpaceException ? (
                        <CheckStats
                          results={{
                            passed: exception.assetsStats.passed,
                            failed: exception.assetsStats.failed,
                            errored: exception.assetsStats.errored,
                          }}
                        />
                      ) : (
                        "Not applicable"
                      )}
                    </TableCell>
                    <TableCell>
                      <Clamp>
                        <Typography
                          component={Link}
                          to={urlLink}
                          sx={{
                            fontSize: 14,
                            fontWeight: 700,
                            lineHeight: "16px",
                          }}
                        >
                          {title}
                        </Typography>
                      </Clamp>
                      <Box display="flex" gap={1} mt={1}>
                        <FirewatchChip
                          key={policyNameTag.key}
                          tag={policyNameTag}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      {isSpaceException
                        ? exception.assetsStats.total
                        : "Not applicable"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </Table>
          </>
        );
      }}
    />
  );
};
